import { useState } from 'react';
import { Button, Dropdown, message } from 'antd';
import type { MenuProps } from 'antd';
import { DownOutlined, DownloadOutlined, FileExcelOutlined, HistoryOutlined, UploadOutlined } from '@ant-design/icons';
import { productService } from '@/services/product.service';
import { exportReport } from '@/utils/exportReport';
import { buildExportRows, buildFixedHeaders } from '../parts/product-export-helpers';
import { useSerialTrackingEnabled } from '@/hooks/useGeneralConfig';
import type { ProductListQuery } from '@/services/queryKeys';
import { ImportSaleworkAmisModal } from './ImportSaleworkAmisModal';
import { ProductImportHistoryDrawer } from './ProductImportHistoryDrawer';

interface ExportProductButtonProps {
  filter: Omit<ProductListQuery, 'page' | 'page_size'>;
  canImport?: boolean;
}

export function ExcelProductButton({ filter, canImport = false }: ExportProductButtonProps) {
  const [loading, setLoading] = useState(false);
  // TCR-2348: modal nhập SP từ Salework + AMIS.
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Chưa resolve setting → file Excel có thể thiếu/thừa cột "Quản lý tồn kho" mà
  // user không hay biết. Chặn export cho tới khi biết chắc.
  const { enabled: serialTrackingEnabled, isLoading: serialSettingLoading } =
    useSerialTrackingEnabled();

  const handleExport = async () => {
    setLoading(true);
    try {
      const { products, variantsByProduct, attrColumns } = await productService.exportList(filter);

      const attrHeaders = attrColumns.map((a) => a.name);
      const dataRows = buildExportRows(products, variantsByProduct, attrColumns, {
        serialTrackingEnabled,
      });

      await exportReport({
        filename: `san-pham-${new Date().toISOString().slice(0, 10)}`,
        format: 'excel',
        title: 'Danh sách sản phẩm',
        sheets: [
          {
            name: 'Sản phẩm',
            columns: [...buildFixedHeaders(serialTrackingEnabled), ...attrHeaders].map((header, idx) => ({
              key: String(idx),
              header,
            })),
            rows: dataRows.map((row) =>
              Object.fromEntries(row.map((cell, idx) => [String(idx), cell])),
            ),
          },
        ],
      });

      void message.success(`Xuất thành công ${products.length} sản phẩm`);
    } catch {
      void message.error('Xuất dữ liệu thất bại');
    } finally {
      setLoading(false);
    }
  };

  const items: MenuProps['items'] = [
    ...(canImport
      ? [
          {
            key: 'import',
            icon: <UploadOutlined />,
            label: 'Nhập Excel',
          },
          {
            key: 'import-salework-amis',
            icon: <UploadOutlined />,
            label: 'Nhập từ Salework / AMIS',
          },
        ]
      : []),
    {
      key: 'import-history',
      icon: <HistoryOutlined />,
      label: 'Lịch sử import',
    },
    {
      key: 'export',
      icon: <DownloadOutlined />,
      label: serialSettingLoading ? 'Đang tải cấu hình...' : 'Xuất Excel',
      disabled: serialSettingLoading,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'import') {
      void message.info('Tính năng đang phát triển');
      return;
    }
    if (key === 'import-salework-amis') {
      setImportOpen(true);
      return;
    }
    if (key === 'import-history') {
      setHistoryOpen(true);
      return;
    }
    if (key === 'export') {
      void handleExport();
    }
  };

  return (
    <>
      <Dropdown menu={{ items, onClick: handleMenuClick }} placement="bottomRight" trigger={['click']}>
        <Button icon={<FileExcelOutlined />} loading={loading} aria-label="Excel">
          Excel <DownOutlined />
        </Button>
      </Dropdown>
      {/* Chỉ mount khi mở: tránh fetch brand/nhóm VTHH/danh mục ở mọi lần vào trang SP. */}
      {canImport && importOpen && <ImportSaleworkAmisModal open onClose={() => setImportOpen(false)} />}
      <ProductImportHistoryDrawer open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

export const ExportProductButton = ExcelProductButton;
